import { Skeleton } from "@mui/material";
import useTranslation from "../../../hooks/useTranslation";
import { Demographic } from "../../../types/workplaceCulture";
import { ENGAGEMENT_THRESHOLD } from "../../../utils/WorkplaceCulture.utils";
import { ScoreCard } from "../../ScoreCard/ScoreCard";
import { Paper } from "../../ui/Paper/Paper";
import { Typography } from "../../ui/Typography/Typography";
import { ReportCustomQuestionChapterScores } from "../ReportCustomQuestionChapterScores/ReportCustomQuestionChapterScores";
import "./ReportCustomQuestionDemographicCard.scss";

interface ReportCustomQuestionDemographicCardProps {
  demographic: Demographic;
  score: number;
  chapterScores: Array<{ id: string; name: string; score: number }>;
  isLoading: boolean;
}

export const ReportCustomQuestionDemographicCard = ({
  demographic,
  score,
  chapterScores,
  isLoading,
}: ReportCustomQuestionDemographicCardProps) => {
  const { t } = useTranslation("reportPage");

  const hasInsufficientEngagement =
    demographic.engagement < ENGAGEMENT_THRESHOLD;

  if (hasInsufficientEngagement) {
    return (
      <div className="ReportCustomQuestionDemographicCard">
        <Paper
          shadow
          className="ReportCustomQuestionDemographicCard__insufficientEngagement"
        >
          <Typography
            tagVariant="h3"
            desktop="h3"
            className="ReportCustomQuestionDemographicCard__insufficientEngagement__title"
            color="darkerStoneBlue"
          >
            {demographic.label}
          </Typography>
          <Typography
            desktop="body2"
            fontStyle="italic"
            color="secondary"
            className="ReportCustomQuestionDemographicCard__insufficientEngagement__message"
          >
            {t("customQuestion.insufficientEngagement", {
              threshold: ENGAGEMENT_THRESHOLD,
            })}
          </Typography>
          <Typography desktop="body2">
            N={demographic.engagement} (
            {(demographic.engagement_percent * 100).toFixed(1)}%)
          </Typography>
        </Paper>
      </div>
    );
  }

  return (
    <div className="ReportCustomQuestionDemographicCard">
      <Paper shadow className="ReportCustomQuestionDemographicCard__content">
        {isLoading ? (
          <Skeleton width="100%" height={150} />
        ) : (
          <div className="ReportCustomQuestionDemographicCard__content__main">
            <ScoreCard
              title={demographic.label}
              score={score}
              scoreSize="small"
              info={
                isLoading ? (
                  <Skeleton width={120} height={24} />
                ) : (
                  <Typography desktop="body2">
                    N={demographic.engagement} (
                    {(demographic.engagement_percent * 100).toFixed(1)}%)
                  </Typography>
                )
              }
            />
          </div>
        )}
        {chapterScores.length > 0 && (
          <ReportCustomQuestionChapterScores
            chapterScores={chapterScores}
            isLoading={isLoading}
          />
        )}
      </Paper>
    </div>
  );
};
